/**
 * IntegrationsSite — admin page that documents the WPForms / website webhook
 * integration. Shows the URL, the secret token, the field mapping and a
 * one-click "Send a test application" tool.
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    CheckCircle2,
    ClipboardCopy,
    Download,
    ExternalLink,
    Globe,
    History,
    KeyRound,
    Loader2,
    Phone,
    PlugZap,
    RefreshCw,
    Send,
    Sparkles,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api, API_BASE, tokenStore } from "../lib/api";


function copyToClipboard(text, label) {
    navigator.clipboard.writeText(text).then(
        () => toast.success(`${label} copié dans le presse-papier`),
        () => toast.error("Impossible de copier"),
    );
}


export default function IntegrationsSite() {
    const [config, setConfig] = useState({ webhook_url: "", token: "", token_masked: "" });
    const [loading, setLoading] = useState(true);
    const [revealToken, setRevealToken] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [recentApps, setRecentApps] = useState([]);

    useEffect(() => {
        api.get("/integrations/site-webhook")
            .then((r) => {
                setConfig(r.data);
            })
            .catch(() => toast.error("Impossible de charger la configuration"))
            .finally(() => setLoading(false));
        loadRecent();
    }, []);

    function loadRecent() {
        api.get("/candidates?source=Site Shariti")
            .then((r) => setRecentApps((r.data || []).slice(0, 5)))
            .catch(() => { });
    }

    async function sendTest() {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.post("/integrations/site-webhook/test");
            setTestResult({ ok: true, ...r.data });
            toast.success("Candidature fictive créée ✅");
            loadRecent();
        } catch (e) {
            setTestResult({ ok: false, error: e.response?.data?.detail || "Erreur" });
            toast.error("Échec du test");
        } finally {
            setTesting(false);
        }
    }

    // ─── Historical sync state ───
    // Persisted in localStorage so the user doesn't lose their settings
    // (WordPress URL + cached forms list) when navigating away from the page.
    const [wpUrl, setWpUrl] = useState(() => {
        try { return localStorage.getItem("prerec.wp_url") || ""; } catch (_e) { return ""; }
    });
    const [formId, setFormId] = useState(0);
    const [daysBack, setDaysBack] = useState(30);
    const [pinging, setPinging] = useState(false);
    const [pingResult, setPingResult] = useState(null);
    const [forms, setForms] = useState(() => {
        try {
            const cached = localStorage.getItem("prerec.wp_forms");
            return cached ? JSON.parse(cached) : [];
        } catch (_e) { return []; }
    });
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [backfilling, setBackfilling] = useState(false);

    // If we have a cached URL, refresh the forms list silently on mount so
    // the dropdown reflects the latest state of the WordPress site.
    useEffect(() => {
        if (!wpUrl) return;
        api.post("/integrations/site-webhook/wp-forms", { wp_base_url: wpUrl })
            .then((r) => {
                const list = r.data?.forms || [];
                setForms(list);
                try { localStorage.setItem("prerec.wp_forms", JSON.stringify(list)); } catch (_e) { /* ignore */ }
            })
            .catch(() => { /* silent — user will see the empty dropdown + helper text */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function pingWordPress() {
        if (!wpUrl) {
            toast.error("Renseignez d'abord l'URL de votre site WordPress");
            return;
        }
        setPinging(true);
        setPingResult(null);
        try {
            const r = await api.post("/integrations/site-webhook/wp-ping", { wp_base_url: wpUrl });
            setPingResult({ ok: true, ...r.data });
            // Persist the URL for next visit (so the dropdown stays populated)
            try { localStorage.setItem("prerec.wp_url", wpUrl); } catch (_e) { /* ignore */ }
            // Auto-fetch forms list once the bridge is reachable
            try {
                const fr = await api.post("/integrations/site-webhook/wp-forms", { wp_base_url: wpUrl });
                const list = fr.data?.forms || [];
                setForms(list);
                try { localStorage.setItem("prerec.wp_forms", JSON.stringify(list)); } catch (_e) { /* ignore */ }
            } catch (_e) { /* ignore — bridge ok but forms call failed */ }
            toast.success("Plugin WordPress détecté ✅");
        } catch (e) {
            setPingResult({ ok: false, error: e.response?.data?.detail || "Erreur" });
        } finally {
            setPinging(false);
        }
    }

    async function runHistoricalSync() {
        if (!wpUrl) {
            toast.error("Renseignez l'URL WordPress");
            return;
        }
        setSyncing(true);
        setSyncResult(null);
        try {
            const r = await api.post("/integrations/site-webhook/historical-sync", {
                wp_base_url: wpUrl,
                form_id: formId,
                days_back: daysBack || null,
            });
            setSyncResult(r.data?.summary || null);
            const s = r.data?.summary || {};
            toast.success(
                `${s.created || 0} nouvelle${(s.created || 0) > 1 ? "s" : ""} candidature${(s.created || 0) > 1 ? "s" : ""} importée${(s.created || 0) > 1 ? "s" : ""}`,
                {
                    description: `${s.merged || 0} doublons fusionnés · ${s.errors || 0} erreur${(s.errors || 0) > 1 ? "s" : ""}`,
                    duration: 8000,
                },
            );
            loadRecent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Synchronisation échouée");
        } finally {
            setSyncing(false);
        }
    }

    function downloadPlugin() {
        // The download endpoint is authenticated → forge a fetch with the bearer
        // token, then trigger a blob download. (Can't use a plain <a href> because
        // it would not carry the JWT.)
        const url = `${API_BASE}/integrations/site-webhook/wp-plugin`;
        fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } })
            .then((res) => {
                if (!res.ok) throw new Error("Téléchargement impossible");
                return res.blob();
            })
            .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = "shariti-prerec-bridge.zip";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(link.href);
                toast.success("Plugin téléchargé ✅");
            })
            .catch(() => toast.error("Téléchargement impossible"));
    }

    async function backfillPhones() {
        setBackfilling(true);
        try {
            const r = await api.post("/integrations/site-webhook/backfill-phones");
            const s = r.data || {};
            toast.success(
                `${s.updated || 0} numéro${(s.updated || 0) > 1 ? "s" : ""} récupéré${(s.updated || 0) > 1 ? "s" : ""}`,
                {
                    description: `Analysés : ${s.attempted || 0} · Sans téléphone trouvé : ${s.failed || 0}`,
                    duration: 8000,
                },
            );
            loadRecent();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Récupération échouée");
        } finally {
            setBackfilling(false);
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    const tokenDisplay = revealToken ? config.token : config.token_masked || "••••••••••••";

    return (
        <div className="max-w-5xl space-y-6" data-testid="integrations-site">
            {/* Header */}
            <header>
                <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
                    <Globe className="h-3 w-3" /> Intégration · Webhook entrant
                </div>
                <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black" data-testid="site-title">
                    Candidatures depuis shariti.com
                </h1>
                <p className="mt-2 text-sm font-medium text-slate-500 max-w-3xl">
                    Chaque candidat qui remplit le formulaire WPForms du site doit remonter automatiquement ici, avec scoring IA, badge dédié et flux d'appels classique.
                </p>
            </header>

            {/* Status */}
            {!config.enabled ? (
                <div className="card-soft p-4 bg-amber-50/60 border-amber-200" data-testid="site-disabled-banner">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                        <div>
                            <div className="text-sm font-extrabold text-amber-900">Webhook désactivé</div>
                            <p className="text-xs text-amber-800 mt-1">La variable d'environnement <code className="bg-amber-100 px-1 rounded">SITE_WEBHOOK_TOKEN</code> n'est pas configurée. Contactez l'équipe technique.</p>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="card-soft p-4 bg-emerald-50/60 border-emerald-200" data-testid="site-enabled-banner">
                    <div className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
                        <div>
                            <div className="text-sm font-extrabold text-emerald-900">Webhook actif — prêt à recevoir des candidatures</div>
                            <p className="text-xs text-emerald-800 mt-1">Il vous reste juste à configurer l'envoi côté WordPress avec l'URL et le token ci-dessous.</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Webhook URL & Token */}
            <div className="card-soft p-6">
                <div className="flex items-center gap-2 mb-4">
                    <PlugZap className="h-5 w-5 text-shariti-orange" />
                    <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600">
                        Endpoint à configurer côté WPForms
                    </h2>
                </div>

                <div className="space-y-4">
                    <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1.5">
                            URL du webhook (méthode POST)
                        </div>
                        <div className="flex items-stretch gap-2">
                            <code className="flex-1 px-3 py-2 rounded-xl bg-shariti-cream border border-shariti-gray font-mono text-xs text-shariti-black break-all" data-testid="site-webhook-url">
                                {config.webhook_url || "—"}
                            </code>
                            <button
                                onClick={() => copyToClipboard(config.webhook_url, "URL")}
                                disabled={!config.webhook_url}
                                data-testid="copy-webhook-url"
                                className="btn-shariti text-xs px-3"
                            >
                                <ClipboardCopy className="h-3.5 w-3.5" />
                                Copier
                            </button>
                        </div>
                    </div>

                    <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                            <KeyRound className="h-3 w-3" /> Token secret (à passer en query string : <code>?token=...</code>)
                        </div>
                        <div className="flex items-stretch gap-2">
                            <code className="flex-1 px-3 py-2 rounded-xl bg-shariti-black text-emerald-300 font-mono text-xs break-all" data-testid="site-webhook-token">
                                {tokenDisplay}
                            </code>
                            <button
                                onClick={() => setRevealToken((s) => !s)}
                                className="px-3 rounded-xl bg-shariti-cream text-slate-700 hover:bg-shariti-gray text-xs font-bold transition"
                                data-testid="toggle-reveal-token"
                            >
                                {revealToken ? "Masquer" : "Révéler"}
                            </button>
                            <button
                                onClick={() => copyToClipboard(config.token, "Token")}
                                disabled={!config.token}
                                data-testid="copy-webhook-token"
                                className="btn-shariti text-xs px-3"
                            >
                                <ClipboardCopy className="h-3.5 w-3.5" />
                                Copier
                            </button>
                        </div>
                        <p className="mt-1.5 text-[10px] font-medium text-slate-400">
                            ⚠️ Ne partagez ce token qu'avec votre webmaster ou Zapier/Make. Toute personne qui le détient peut créer des candidats dans PREREC.
                        </p>
                    </div>
                </div>
            </div>

            {/* Field mapping */}
            <div className="card-soft p-6">
                <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600 mb-3">
                    Mapping des champs (clé JSON → champ PREREC)
                </h2>
                <p className="text-xs text-slate-500 mb-4">
                    Dans WPForms (ou Zapier / Make), envoyez un POST JSON avec ces clés. Aucune n'est obligatoire sauf <code className="bg-shariti-cream px-1 rounded">email</code> + nom.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="site-mapping-table">
                        <thead>
                            <tr className="border-b border-shariti-gray text-left text-[10px] font-extrabold uppercase tracking-widest text-slate-400">
                                <th className="py-2 pr-4">Clé JSON</th>
                                <th className="py-2 pr-4">Alias accepté</th>
                                <th className="py-2 pr-4">Type</th>
                                <th className="py-2">Champ PREREC</th>
                            </tr>
                        </thead>
                        <tbody className="text-slate-700">
                            <Row k="first_name" alias="prenom" type="string" prerec="Prénom" />
                            <Row k="last_name" alias="nom" type="string" prerec="Nom" />
                            <Row k="full_name" alias="nom_complet" type="string" prerec="Nom + Prénom (fallback)" />
                            <Row k="email" alias="—" type="email *" prerec="Email" />
                            <Row k="phone" alias="telephone" type="string" prerec="Téléphone" />
                            <Row k="address" alias="adresse" type="string" prerec="Adresse postale" />
                            <Row k="city" alias="ville" type="string" prerec="Ville (sinon auto-extraite)" />
                            <Row k="message" alias="motivation" type="string" prerec="Message + utilisé par l'IA pour le scoring" />
                            <Row k="additional_info" alias="informations_complementaires" type="string" prerec="Infos complémentaires" />
                            <Row k="newsletter_consent" alias="newsletter" type="bool" prerec="Consentement newsletter" />
                            <Row k="applied_at" alias="date_candidature" type="ISO datetime" prerec="Date de candidature (sinon = maintenant)" />
                            <Row k="cv_url" alias="—" type="url" prerec="Lien CV (recommandé via WPForms)" />
                            <Row k="cv_pdf_base64" alias="cv_pdf" type="base64 PDF" prerec="CV stocké inline (alternative)" />
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Setup steps */}
            <div className="card-soft p-6">
                <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600 mb-3">
                    Étapes côté WordPress
                </h2>
                <ol className="space-y-3 text-sm text-slate-700">
                    <Step n={1}>
                        Installer l'<strong>addon Webhooks</strong> de WPForms (ou utiliser <a href="https://zapier.com" target="_blank" rel="noopener noreferrer" className="text-shariti-orange font-bold underline">Zapier</a> / <a href="https://make.com" target="_blank" rel="noopener noreferrer" className="text-shariti-orange font-bold underline">Make</a> si vous êtes sur la version gratuite de WPForms).
                    </Step>
                    <Step n={2}>
                        Dans le formulaire "Nouvelle candidature", ouvrir <em>Settings → Webhooks → Add Webhook</em>.
                    </Step>
                    <Step n={3}>
                        Coller l'URL ci-dessus dans <em>Request URL</em>, choisir <em>POST</em>, <em>JSON</em>, et mapper chaque champ WPForms vers la clé JSON correspondante du tableau ci-dessus.
                    </Step>
                    <Step n={4}>
                        Activer le webhook et soumettre une candidature de test depuis shariti.com → elle doit apparaître ici dans les 5 secondes.
                    </Step>
                    <Step n={5}>
                        En cas de souci, utilisez le bouton <strong>Envoyer un test</strong> ci-dessous pour vérifier que l'endpoint répond bien sans toucher au formulaire WordPress.
                    </Step>
                </ol>
            </div>

            {/* Test button */}
            <div className="card-soft p-6">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600 mb-1 flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-shariti-orange" /> Tester avec une candidature fictive
                        </h2>
                        <p className="text-xs text-slate-500 max-w-md">
                            Crée un candidat "Test SiteWebhook" avec un message bidon. Permet de vérifier la chaîne sans toucher au site WordPress.
                        </p>
                    </div>
                    <button
                        onClick={sendTest}
                        disabled={testing || !config.enabled}
                        data-testid="send-test-application"
                        className="btn-shariti text-xs"
                    >
                        {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                        Envoyer un test
                    </button>
                </div>
                {testResult && (
                    <motion.div
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-4 rounded-xl border px-3 py-2 text-xs ${testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-shariti-red"}`}
                        data-testid="test-result"
                    >
                        {testResult.ok ? (
                            <>✅ Candidature créée — id <code className="font-mono">{testResult.candidate_id}</code></>
                        ) : (
                            <>❌ Erreur : {testResult.error}</>
                        )}
                    </motion.div>
                )}
            </div>

            {/* Historical sync */}
            <div className="card-soft p-6" data-testid="site-historical-sync">
                <div className="flex items-center gap-2 mb-2">
                    <History className="h-5 w-5 text-shariti-orange" />
                    <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600">
                        Synchroniser l'historique WPForms
                    </h2>
                </div>
                <p className="text-xs text-slate-500 mb-4 max-w-3xl">
                    Le webhook capture les <strong>nouvelles</strong> candidatures en temps réel. Pour rattraper celles qui étaient déjà dans WPForms avant son activation, installez le mini-plugin compagnon sur WordPress puis lancez une synchronisation ponctuelle.
                </p>

                {/* Step 1: install plugin */}
                <div className="rounded-2xl border border-shariti-gray bg-shariti-cream/40 p-4 mb-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                            <span className="h-7 w-7 rounded-full bg-shariti-orange text-white font-extrabold text-xs flex items-center justify-center shrink-0">1</span>
                            <div>
                                <div className="text-sm font-extrabold text-shariti-black">Installer le plugin compagnon</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">Téléchargez le <code>.zip</code>, puis dans WordPress allez dans <em>Extensions → Ajouter → Téléverser une extension</em>, sélectionnez le fichier, activez-le, et collez votre token dans <em>Réglages → Shariti PREREC</em>.</div>
                            </div>
                        </div>
                        <button
                            onClick={downloadPlugin}
                            data-testid="download-wp-plugin"
                            className="btn-shariti text-xs"
                        >
                            <Download className="h-3.5 w-3.5" />
                            Télécharger le plugin (.zip)
                        </button>
                    </div>

                    {/* Token to paste in WordPress > Réglages > Shariti PREREC */}
                    <div className="mt-4 pt-4 border-t border-shariti-gray">
                        <div className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                            <KeyRound className="h-3 w-3" />
                            Token à coller dans WordPress (Réglages → Shariti PREREC)
                        </div>
                        <div className="flex items-stretch gap-2">
                            <code
                                className="flex-1 px-3 py-2 rounded-xl bg-shariti-black text-emerald-300 font-mono text-xs break-all"
                                data-testid="site-webhook-token-step1"
                            >
                                {config.token || "—"}
                            </code>
                            <button
                                onClick={() => copyToClipboard(config.token, "Token")}
                                disabled={!config.token}
                                data-testid="copy-token-step1"
                                className="btn-shariti text-xs px-3"
                            >
                                <ClipboardCopy className="h-3.5 w-3.5" />
                                Copier le token
                            </button>
                        </div>
                        <p className="mt-1.5 text-[10px] font-medium text-slate-500">
                            Collez-le tel quel dans le champ <em>Token Shariti</em> de la page de réglages du plugin, puis cliquez <em>Enregistrer</em>.
                        </p>
                    </div>
                </div>

                {/* Step 2: connect */}
                <div className="rounded-2xl border border-shariti-gray bg-shariti-cream/40 p-4 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                        <span className="h-7 w-7 rounded-full bg-shariti-orange text-white font-extrabold text-xs flex items-center justify-center shrink-0">2</span>
                        <div className="text-sm font-extrabold text-shariti-black">Connecter à votre WordPress</div>
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <label className="flex-1 min-w-[240px] block">
                            <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1 block">
                                URL de votre site WordPress
                            </span>
                            <input
                                type="url"
                                value={wpUrl}
                                onChange={(e) => setWpUrl(e.target.value)}
                                placeholder="https://shariti.com"
                                className="input-shariti w-full"
                                data-testid="wp-url-input"
                            />
                        </label>
                        <button
                            onClick={pingWordPress}
                            disabled={pinging || !wpUrl}
                            data-testid="wp-ping-button"
                            className="btn-shariti text-xs"
                        >
                            {pinging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlugZap className="h-3.5 w-3.5" />}
                            Vérifier la connexion
                        </button>
                    </div>
                    {pingResult && (
                        <div
                            className={`mt-3 rounded-xl border px-3 py-2 text-xs ${pingResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-shariti-red"}`}
                            data-testid="wp-ping-result"
                        >
                            {pingResult.ok ? (
                                <>✅ Plugin détecté · version {pingResult.wordpress?.version || "?"}</>
                            ) : (
                                <>❌ {pingResult.error}</>
                            )}
                        </div>
                    )}
                </div>

                {/* Step 3: launch sync */}
                <div className="rounded-2xl border border-shariti-gray bg-shariti-cream/40 p-4">
                    <div className="flex items-center gap-3 mb-3">
                        <span className="h-7 w-7 rounded-full bg-shariti-orange text-white font-extrabold text-xs flex items-center justify-center shrink-0">3</span>
                        <div className="text-sm font-extrabold text-shariti-black">Lancer la synchronisation</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                        <label className="block">
                            <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1 block">
                                Formulaire WPForms
                            </span>
                            <select
                                value={formId}
                                onChange={(e) => setFormId(parseInt(e.target.value, 10) || 0)}
                                className="input-shariti w-full"
                                data-testid="wp-form-select"
                            >
                                <option value={0}>Tous les formulaires</option>
                                {forms.map((f) => (
                                    <option key={f.id} value={f.id}>#{f.id} · {f.title}</option>
                                ))}
                            </select>
                            {forms.length === 0 && (
                                <p className="text-[10px] text-amber-700 mt-1 leading-tight">
                                    Cliquez sur <strong>Vérifier la connexion</strong> à l'étape 2 pour charger la liste des formulaires de votre site (Candidatures spontanées, Contact, etc.).
                                </p>
                            )}
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mb-1 block">
                                Période à importer
                            </span>
                            <select
                                value={daysBack || 0}
                                onChange={(e) => setDaysBack(parseInt(e.target.value, 10) || 0)}
                                className="input-shariti w-full"
                                data-testid="wp-period-select"
                            >
                                <option value={7}>7 derniers jours</option>
                                <option value={30}>30 derniers jours</option>
                                <option value={90}>90 derniers jours</option>
                                <option value={180}>6 derniers mois</option>
                                <option value={365}>12 derniers mois</option>
                                <option value={0}>Tout l'historique</option>
                            </select>
                        </label>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <button
                            onClick={runHistoricalSync}
                            disabled={syncing || !wpUrl}
                            data-testid="wp-historical-sync-button"
                            className="inline-flex items-center gap-2 bg-shariti-orange text-white font-extrabold text-xs uppercase tracking-wider px-5 py-2.5 rounded-full hover:bg-[#d66528] disabled:opacity-50 transition shadow-btn"
                        >
                            {syncing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                            {syncing ? "Synchronisation en cours…" : "Lancer la synchronisation"}
                        </button>
                        <span className="text-[11px] text-slate-400">
                            La déduplication par email s'applique : aucun doublon ne sera créé.
                        </span>
                    </div>

                    {syncResult && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2 text-center"
                            data-testid="sync-summary"
                        >
                            <StatPill label="Récupérées" value={syncResult.fetched} tone="slate" />
                            <StatPill label="Créées" value={syncResult.created} tone="emerald" />
                            <StatPill label="Fusionnées" value={syncResult.merged} tone="amber" />
                            <StatPill label="Erreurs" value={syncResult.errors} tone="red" />
                            <StatPill label="Ignorées" value={syncResult.skipped} tone="slate" />
                        </motion.div>
                    )}

                    {/* Backfill phone numbers from PDF CVs */}
                    <div className="mt-5 pt-5 border-t border-shariti-gray">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="text-sm font-extrabold text-shariti-black flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-amber-600" />
                                    Récupérer les téléphones depuis les CV
                                </div>
                                <p className="text-[11px] text-slate-500 mt-0.5 max-w-md">
                                    Si certains candidats Site Shariti n'ont pas de numéro (problème de mapping WPForms), PREREC analyse leur CV PDF et extrait automatiquement le téléphone via OCR de texte.
                                </p>
                            </div>
                            <button
                                onClick={backfillPhones}
                                disabled={backfilling}
                                data-testid="backfill-phones-btn"
                                className="inline-flex items-center gap-2 bg-amber-500 text-white font-extrabold text-xs uppercase tracking-wider px-4 py-2.5 rounded-full hover:bg-amber-600 disabled:opacity-50 transition"
                            >
                                {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Phone className="h-3.5 w-3.5" />}
                                {backfilling ? "Analyse en cours…" : "Lancer l'analyse"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Recent applications */}
            <div className="card-soft p-6">
                <h2 className="text-sm font-extrabold uppercase tracking-widest text-slate-600 mb-3">
                    Dernières candidatures site
                </h2>
                {recentApps.length === 0 ? (
                    <p className="text-xs text-slate-400">Aucune candidature site reçue pour l'instant.</p>
                ) : (
                    <div className="space-y-2">
                        {recentApps.map((c) => (
                            <a
                                key={c.id}
                                href={`/candidats/${c.id}`}
                                className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-shariti-cream transition group"
                                data-testid={`recent-site-${c.id}`}
                            >
                                <span className="text-xl">🌐</span>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-extrabold text-shariti-black truncate group-hover:text-shariti-orange transition">
                                        {c.first_name} {c.last_name}
                                    </div>
                                    <div className="text-[11px] text-slate-500 truncate">{c.email} · score {c.ai_score ?? "—"}/100</div>
                                </div>
                                <ExternalLink className="h-3.5 w-3.5 text-slate-300 group-hover:text-shariti-orange transition" />
                            </a>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}



function StatPill({ label, value, tone }) {
    const palettes = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
        amber: "bg-amber-50 border-amber-200 text-amber-800",
        red: "bg-red-50 border-red-200 text-shariti-red",
    };
    return (
        <div className={`rounded-xl border px-2 py-2 ${palettes[tone] || palettes.slate}`}>
            <div className="text-lg font-black">{value ?? 0}</div>
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
        </div>
    );
}


function Row({ k, alias, type, prerec }) {
    return (
        <tr className="border-b border-shariti-gray/40 last:border-0">
            <td className="py-2 pr-4 font-mono font-bold text-shariti-orange">{k}</td>
            <td className="py-2 pr-4 font-mono text-slate-400">{alias}</td>
            <td className="py-2 pr-4 text-slate-500">{type}</td>
            <td className="py-2 font-medium">{prerec}</td>
        </tr>
    );
}

function Step({ n, children }) {
    return (
        <li className="flex gap-3">
            <span className="h-6 w-6 rounded-full bg-shariti-orange text-white font-extrabold text-xs flex items-center justify-center shrink-0">
                {n}
            </span>
            <div className="text-slate-700 leading-relaxed">{children}</div>
        </li>
    );
}
